import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowUpRight, MessageCircle, ShieldAlert, AlertTriangle, EyeOff, MapPin, Cctv,
  ScanSearch, Siren, Zap, Route, Bell, RefreshCw, SlidersHorizontal, Brain, Target, Waypoints,
  Cpu, ShieldCheck, HardHat, UserCheck, Timer, Factory, Cog, Plane, Warehouse, Lock, CircleDot,
  Ban, TrafficCone, ClipboardCheck, TrendingDown, Building2, Landmark, Briefcase, Car, Package,
  Clock, CameraOff, Fence, Camera, Activity, Move, BellRing, FileSearch, Shirt, Container,
  Boxes, Layers, Truck, TrendingUp,
} from "lucide-react";
import { Reveal } from "../../components/Reveal";
import { WA_NUMBER } from "../../data/site";

const ICONS = {
  shieldalert: ShieldAlert, alerttriangle: AlertTriangle, eyeoff: EyeOff, mappin: MapPin, cctv: Cctv,
  scansearch: ScanSearch, siren: Siren, zap: Zap, route: Route, bell: Bell, refreshcw: RefreshCw,
  sliders: SlidersHorizontal, brain: Brain, target: Target, waypoints: Waypoints, cpu: Cpu,
  shieldcheck: ShieldCheck, hardhat: HardHat, usercheck: UserCheck, timer: Timer, factory: Factory,
  cog: Cog, plane: Plane, warehouse: Warehouse, lock: Lock, circledot: CircleDot, ban: Ban,
  trafficcone: TrafficCone, clipboardcheck: ClipboardCheck, trendingdown: TrendingDown,
  building2: Building2, landmark: Landmark, briefcase: Briefcase, car: Car, package: Package,
  clock: Clock, cameraoff: CameraOff, fence: Fence, camera: Camera, activity: Activity, move: Move,
  bellring: BellRing, filesearch: FileSearch, shirt: Shirt, container: Container,
  boxes: Boxes, layers: Layers, truck: Truck, trendingup: TrendingUp,
};
const Icon = ({ name, ...props }) => {
  const C = ICONS[name] || ScanSearch;
  return <C {...props} />;
};

const BlockTitle = ({ kicker, children }) => (
  <Reveal>
    <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-smipl-orange mb-3">{kicker}</p>
    <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight mb-10">{children}</h2>
  </Reveal>
);

const ProductDetail = ({ data: d, openContact }) => {
  const t = d.shortName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <main className="pt-36 pb-28 min-h-screen" data-testid={`product-detail-${t}`}>
      <div className="max-w-6xl mx-auto px-6">
        <Reveal>
          <Link to="/products" data-testid="pd-back-link" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-slate-500 hover:text-smipl-orange transition-colors">
            <ArrowLeft size={13} /> Our Products <span className="text-white/20">/</span>
            <span className="text-slate-400 normal-case tracking-normal">{d.shortName}</span>
          </Link>
        </Reveal>

        <div className="mt-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal delay={0.05}>
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-smipl-orange">{d.category}</p>
            </Reveal>
            <Reveal delay={0.12}>
              <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]">{d.heroTitle}</h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed">{d.description}</p>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-7 flex flex-wrap gap-2.5">
                {d.highlights.map((h) => (
                  <span key={h} className="rounded-full border border-white/12 bg-white/[0.04] px-4 py-2 text-xs font-medium text-slate-200">{h}</span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.35}>
              <div className="mt-9 flex flex-wrap gap-4">
                <button data-testid="pd-talk-btn" onClick={openContact} className="flex items-center gap-2 rounded-full bg-smipl-orange text-white px-7 py-3.5 text-sm font-semibold hover:bg-smipl-amber hover:glow-orange transition-all duration-300">
                  Discuss Your Requirement <ArrowUpRight size={15} />
                </button>
                <Link to="/products" data-testid="pd-back-btn" className="flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white hover:border-smipl-orange hover:text-smipl-orange transition-all duration-300">
                  <ArrowLeft size={15} /> Back to Products
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.25}>
            <div className="relative rounded-3xl overflow-hidden border border-white/12 shadow-2xl shadow-black/50">
              <img src={d.heroImg} alt={d.heroImgAlt} className="w-full object-cover" />
              <motion.div
                initial={{ top: "-10%" }}
                animate={{ top: "110%" }}
                transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }}
                className="absolute left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-smipl-orange to-transparent opacity-80"
              />
              <div className="absolute top-4 left-4 rounded-full bg-navy-deep/80 backdrop-blur px-3.5 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-smipl-orange border border-smipl-orange/30 flex items-center gap-2">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-pulse-ring absolute inline-flex h-full w-full rounded-full bg-smipl-orange" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-smipl-orange" />
                </span>
                {d.heroBadge}
              </div>
            </div>
          </Reveal>
        </div>

        <section className="mt-28" data-testid="pd-challenge">
          <BlockTitle kicker="The Problem">The Challenge</BlockTitle>
          <Reveal delay={0.05}>
            <p className="max-w-3xl text-slate-400 text-base leading-relaxed mb-10">{d.challengeIntro}</p>
          </Reveal>
          <div className={`grid gap-5 ${d.challenges.length > 3 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
            {d.challenges.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="tracing-border h-full rounded-2xl border border-white/8 bg-navy-deep/60 p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-500/10 border border-red-500/25 text-red-400">
                    <Icon name={c.icon} size={19} />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold">{c.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mt-28" data-testid="pd-solution">
          <div className={`rounded-3xl border border-smipl-orange/25 bg-gradient-to-br from-smipl-orange/[0.08] to-transparent p-10 md:p-14 ${d.solutionImg ? "grid lg:grid-cols-2 gap-10 items-center" : ""}`}>
            <div>
              <BlockTitle kicker="The Solution">{d.solutionTitle}</BlockTitle>
              <Reveal delay={0.08}>
                <div className="space-y-4 text-slate-300 text-base leading-relaxed -mt-4">
                  {d.solutionParas.map((p) => <p key={p.slice(0, 32)}>{p}</p>)}
                </div>
              </Reveal>
            </div>
            {d.solutionImg && (
              <Reveal delay={0.15}>
                <div className="relative rounded-2xl overflow-hidden border border-white/12 max-w-md mx-auto w-full">
                  <img src={d.solutionImg} alt={d.solutionImgAlt} className="w-full object-cover" />
                  {d.solutionBadge && (
                    <div className="absolute bottom-4 right-4 rounded-full bg-navy-deep/80 backdrop-blur px-3.5 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-smipl-orange border border-smipl-orange/30">
                      {d.solutionBadge}
                    </div>
                  )}
                </div>
              </Reveal>
            )}
          </div>
        </section>

        <section className="mt-28" data-testid="pd-workflow">
          <BlockTitle kicker="Workflow">How the System Works</BlockTitle>
          <div className="relative">
            <div className="hidden lg:block absolute top-[52px] left-0 right-0 h-px bg-white/10" />
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
              className="hidden lg:block absolute top-[52px] left-0 right-0 h-px bg-smipl-orange origin-left"
            />
            <div className={`grid sm:grid-cols-2 gap-5 ${d.workflow.length > 5 ? "lg:grid-cols-6" : "lg:grid-cols-5"}`}>
              {d.workflow.map((w, i) => (
                <Reveal key={w.step} delay={i * 0.1}>
                  <div data-testid={`pd-step-${i + 1}`} className="relative rounded-2xl border border-white/8 bg-navy-deep/60 p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                    <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-smipl-orange text-navy-deep font-mono text-xs font-bold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <Icon name={w.icon} size={20} className="mt-5 text-smipl-orange" />
                    <h3 className="mt-3 font-display text-base font-bold">{w.step}</h3>
                    <p className="mt-2 text-xs text-slate-400 leading-relaxed">{w.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {d.gallery?.length > 0 && (
          <section className="mt-28" data-testid="pd-gallery">
            <BlockTitle kicker="Product Screens">See It in Action</BlockTitle>
            <div className="grid md:grid-cols-3 gap-5">
              {d.gallery.map((g, i) => (
                <Reveal key={g.img} delay={i * 0.08}>
                  <div className="relative rounded-2xl overflow-hidden border border-white/12 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                    <img src={g.img} alt={g.alt} className="w-full object-cover" loading="lazy" />
                    {g.badge && (
                      <div className="absolute bottom-3 left-3 rounded-full bg-navy-deep/85 backdrop-blur px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-smipl-orange border border-smipl-orange/30">
                        {g.badge}
                      </div>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        <section className="mt-28" data-testid="pd-capabilities">
          <BlockTitle kicker="Capabilities">Key Capabilities</BlockTitle>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {d.capabilities.map((c, i) => (
              <Reveal key={c.title} delay={(i % 3) * 0.07}>
                <div className="tracing-border group h-full rounded-2xl border border-white/8 bg-navy-deep/60 p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-smipl-orange/10 border border-smipl-orange/25 text-smipl-orange group-hover:bg-smipl-orange group-hover:text-white transition-all duration-300">
                    <Icon name={c.icon} size={19} />
                  </div>
                  <h3 className="mt-5 font-display text-base font-bold">{c.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mt-28" data-testid="pd-technology">
          <BlockTitle kicker="Technology">Technology Behind the Solution</BlockTitle>
          <div className="grid sm:grid-cols-2 gap-5">
            {d.tech.map((x, i) => (
              <Reveal key={x.title} delay={(i % 2) * 0.08}>
                <div className="h-full rounded-2xl border border-white/8 bg-gradient-to-br from-white/[0.05] to-transparent p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                  <Icon name={x.icon} size={26} className="text-smipl-orange" />
                  <h3 className="mt-5 font-display text-lg font-bold font-mono tracking-wide">{x.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{x.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mt-28" data-testid="pd-impact">
          <BlockTitle kicker="Business Impact">{d.impactTitle}</BlockTitle>
          <div className="grid sm:grid-cols-2 gap-5">
            {d.impact.map((c, i) => (
              <Reveal key={c.title} delay={(i % 2) * 0.08}>
                <div className="group h-full rounded-2xl border border-white/8 bg-gradient-to-br from-white/[0.05] to-transparent p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                  <Icon name={c.icon} size={26} className="text-smipl-orange" />
                  <h3 className="mt-5 font-display text-xl font-bold">{c.title}</h3>
                  <p className="mt-2 text-sm md:text-base text-slate-400 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mt-28" data-testid="pd-deployments">
          <BlockTitle kicker="Deployments">Where It Can Be Deployed</BlockTitle>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {d.deployments.map((x, i) => (
              <Reveal key={x.label} delay={(i % 3) * 0.06}>
                <div className="flex items-center gap-3.5 rounded-2xl border border-white/8 bg-white/[0.03] px-5 py-5 transition-all duration-300 hover:border-smipl-orange/40 hover:-translate-y-0.5">
                  <Icon name={x.icon} size={18} className="shrink-0 text-smipl-orange" />
                  <span className="text-sm font-medium text-slate-200">{x.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <Reveal delay={0.1}>
          <div className="mt-28 rounded-3xl border border-smipl-orange/25 bg-gradient-to-br from-smipl-orange/10 to-transparent p-10 md:p-14 text-center" data-testid="pd-cta">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              {d.ctaTitle.split(" ").map((w, i) => (i >= Math.ceil(d.ctaTitle.split(" ").length / 2) ? <span key={i} className="text-smipl-orange">{w} </span> : w + " "))}
            </h2>
            <p className="mt-5 max-w-xl mx-auto text-slate-400 text-base leading-relaxed">{d.ctaDesc}</p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <button data-testid="pd-experts-btn" onClick={openContact} className="flex items-center gap-2 rounded-full bg-smipl-orange text-white px-8 py-4 text-sm font-semibold hover:bg-smipl-amber hover:glow-orange transition-all duration-300">
                Talk to Our Experts <ArrowUpRight size={15} />
              </button>
              <button
                data-testid="pd-whatsapp-btn"
                onClick={() => (WA_NUMBER ? window.open(`https://wa.me/${WA_NUMBER}`, "_blank") : openContact())}
                className="flex items-center gap-2 rounded-full border border-wa/50 text-wa px-8 py-4 text-sm font-semibold hover:bg-wa hover:text-navy-deep transition-all duration-300"
              >
                <MessageCircle size={15} /> WhatsApp
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export default ProductDetail;
